import React, { useState, useMemo, useCallback } from 'react';
import { CheckCircle, XCircle, Eye, Image as ImageIcon, MapPin } from 'lucide-react';
import FilterBar from './FilterBar';
import StaffDateGate, { gateScopeParams } from './StaffDateGate';
import useAutoRefresh from '../../lib/useAutoRefresh';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_TABS = [
  { id: 'pending', label: 'Pending', tone: 'bg-orange-100 text-orange-700 border-orange-300' },
  { id: 'approved', label: 'Approved', tone: 'bg-emerald-100 text-emerald-700 border-emerald-300' },
  { id: 'rejected', label: 'Rejected', tone: 'bg-red-100 text-red-700 border-red-300' },
  { id: 'all', label: 'All', tone: 'bg-blue-100 text-blue-700 border-blue-300' },
];

const STATUS_BADGE = {
  pending: 'bg-orange-100 text-orange-700',
  approved: 'bg-emerald-100 text-emerald-700',
  rejected: 'bg-red-100 text-red-700',
};

/**
 * Normalise the saved photo to a renderable `src`. Older records may have
 * stored just the raw base64 payload without the `data:` prefix — handle
 * both gracefully.
 */
const photoSrc = (raw) => {
  if (!raw) return null;
  if (typeof raw !== 'string') return null;
  if (raw.startsWith('data:')) return raw;
  if (raw.startsWith('http')) return raw;
  // Assume JPEG since that's what the staff camera capture produces.
  return `data:image/jpeg;base64,${raw}`;
};

export default function PermissionApprovals() {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [statusTab, setStatusTab] = useState('pending');
  const [editing, setEditing] = useState(null);
  const [hrs, setHrs] = useState('');
  const [previewPhoto, setPreviewPhoto] = useState(null);
  const [search, setSearch] = useState('');
  const [dateRange, setDateRange] = useState({ from: '', to: '' });
  const [gate, setGate] = useState({ geo_fence_id: null, geo_fence_name: null, staff: null, from: '', to: '' });

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return records.filter(r => {
      if (dateRange.from || dateRange.to) {
        if (!r.date) return false;
        if (dateRange.from && r.date < dateRange.from) return false;
        if (dateRange.to && r.date > dateRange.to) return false;
      }
      if (q && !(
        (r.staff_name || '').toLowerCase().includes(q) ||
        (r.reason || '').toLowerCase().includes(q)
      )) return false;
      return true;
    });
  }, [records, search, dateRange]);

  // Counts for each tab (calculated against the loaded set so admins can
  // see Pending / Approved / Rejected totals at a glance).
  const counts = useMemo(() => {
    const c = { pending: 0, approved: 0, rejected: 0, all: records.length };
    for (const r of records) {
      const st = r.approval?.status;
      if (st === 'pending') c.pending++;
      else if (st === 'approved') c.approved++;
      else if (st === 'rejected') c.rejected++;
    }
    return c;
  }, [records]);

  const fetchAll = useCallback(async () => {
    const scope = gateScopeParams(gate);
    if (!scope) return;
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams(scope);
      if (statusTab !== 'all') params.append('status', statusTab);
      const r = await fetch(`${API_URL}/api/admin/permissions?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const d = r.ok ? await r.json() : [];
      setRecords(d);
    } finally {
      setLoading(false);
    }
  }, [gate.geo_fence_id, gate.staff?.id, gate.from, gate.to, statusTab]);

  const applyGate = async () => {
    setLoading(true); setHasLoaded(true);
    await fetchAll();
  };

  // Refetch when tab changes after first load.
  React.useEffect(() => { if (hasLoaded) fetchAll(); }, [statusTab, fetchAll, hasLoaded]);

  // Silent 30-sec refresh — only after filters are applied.
  useAutoRefresh(async () => { if (hasLoaded) await fetchAll(); }, 30_000);

  const action = async (id, act) => {
    const token = localStorage.getItem('token');
    const body = { action: act };
    if (act === 'approve') body.approved_hours = parseFloat(hrs || 0);
    const r = await fetch(`${API_URL}/api/admin/permissions/${id}/approve`, {
      method: 'PUT',
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    });
    if (r.ok) {
      const d = await r.json();
      alert(`${act}. Penalty: ${d.penalty_hours}h`);
      setEditing(null); setHrs('');
      fetchAll();
    } else { alert('Failed'); }
  };

  return (
    <div data-testid="permission-approvals">
      <h1 className="text-2xl font-bold text-gray-900 mb-4">
        Permission Approvals {hasLoaded && `(${filtered.length}/${records.length})`}
      </h1>

      <div className="mb-4">
        <StaffDateGate value={gate} onChange={setGate} onApply={applyGate} loading={loading} defaultDays={30} />
      </div>

      {!hasLoaded && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-8 text-center text-blue-700" data-testid="permission-prompt">
          <Eye className="mx-auto mb-2 opacity-60" size={28} />
          <p className="font-medium">Pick a staff and date range, then tap <strong>Load</strong>.</p>
          <p className="text-xs text-blue-600 mt-1">Filter by status (Pending / Approved / Rejected) after loading.</p>
        </div>
      )}

      {hasLoaded && (
        <div className="flex flex-wrap gap-2 mb-3" data-testid="permission-status-tabs">
          {STATUS_TABS.map(t => (
            <button
              key={t.id}
              onClick={() => setStatusTab(t.id)}
              className={`px-3 py-1.5 rounded-lg text-sm border ${
                statusTab === t.id
                  ? `${t.tone} font-semibold`
                  : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
              }`}
              data-testid={`permission-tab-${t.id}`}
            >
              {t.label} <span className="ml-1 opacity-70">({counts[t.id] ?? 0})</span>
            </button>
          ))}
        </div>
      )}

      {hasLoaded && (
        <FilterBar
          testId="permission-filters"
          placeholder="Search by staff name or reason…"
          search={search}
          onSearch={setSearch}
          dateRange={dateRange}
          onDateChange={setDateRange}
          onClear={() => { setSearch(''); setDateRange({ from: '', to: '' }); }}
          resultCount={filtered.length}
        />
      )}

      {hasLoaded && filtered.length === 0 && (
        <div className="bg-white rounded-xl p-8 text-center text-gray-500">
          {records.length === 0 ? `No ${statusTab} permissions in this window.` : 'No records match the current filter'}
        </div>
      )}

      {hasLoaded && (
      <div className="space-y-3">
        {filtered.map(r => {
          const st = r.approval?.status || 'pending';
          const photo = photoSrc(r.approval_slip_photo);
          const gps = r.approval_slip_gps;
          return (
            <div key={r.id} className="bg-white rounded-xl p-5 shadow-sm border" data-testid={`permission-${r.id}`}>
              <div className="flex items-start justify-between mb-2 gap-3">
                <div className="min-w-0">
                  <h3 className="font-semibold truncate">{r.staff_name}</h3>
                  <p className="text-xs text-gray-500">{r.date}</p>
                </div>
                <span className={`px-2 py-1 text-xs rounded-full whitespace-nowrap ${STATUS_BADGE[st] || 'bg-gray-100 text-gray-700'}`}>{st}</span>
              </div>

              <p className="text-sm text-gray-700 mb-3">📝 {r.reason || '(no reason)'}</p>

              <div className="grid grid-cols-1 md:grid-cols-[120px,1fr] gap-3 mb-3 items-start">
                {/* Inline image thumbnail — clickable to enlarge */}
                <div>
                  {photo ? (
                    <button
                      onClick={() => setPreviewPhoto(photo)}
                      className="block w-[120px] h-[90px] rounded-lg overflow-hidden border bg-gray-50 hover:ring-2 hover:ring-blue-400"
                      data-testid={`permission-thumb-${r.id}`}
                      title="Tap to enlarge"
                    >
                      <img src={photo} alt="Permission slip" className="w-full h-full object-cover" />
                    </button>
                  ) : (
                    <div className="w-[120px] h-[90px] rounded-lg border border-dashed bg-gray-50 flex flex-col items-center justify-center text-gray-400 text-[10px] text-center" data-testid={`permission-nophoto-${r.id}`}>
                      <ImageIcon size={20} className="mb-1 opacity-50" />
                      No photo
                    </div>
                  )}
                  {gps && gps.latitude && (
                    <a
                      href={`https://maps.google.com/?q=${gps.latitude},${gps.longitude}`}
                      target="_blank" rel="noreferrer"
                      className="mt-1 inline-flex items-center gap-1 text-[11px] text-blue-600 hover:underline"
                      data-testid={`permission-gps-${r.id}`}
                    >
                      <MapPin size={11} /> Photo GPS
                    </a>
                  )}
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                  <div>
                    <p className="text-xs text-gray-500">Requested</p>
                    <p className="font-medium">{r.requested_hours ?? '-'}h</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Actual</p>
                    <p className="font-medium">{r.actual_hours || '-'}h</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Approved</p>
                    <p className="font-medium text-emerald-700">{r.approval?.approved_hours ?? 0}h</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Penalty</p>
                    <p className={`font-medium ${(r.approval?.penalty_hours || 0) > 0 ? 'text-red-600' : 'text-gray-700'}`}>
                      {r.approval?.penalty_hours ?? 0}h
                    </p>
                  </div>
                  {r.approval?.notes && (
                    <div className="col-span-2 md:col-span-4 text-xs bg-gray-50 rounded p-2 text-gray-600">
                      📝 {r.approval.notes}
                    </div>
                  )}
                </div>
              </div>

              {st === 'pending' && (
                editing === r.id ? (
                  <div className="space-y-2 p-3 bg-gray-50 rounded-lg">
                    <input
                      type="number" step="0.1" placeholder="Approved hrs"
                      value={hrs} onChange={(e) => setHrs(e.target.value)}
                      className="w-full px-3 py-2 border rounded text-sm"
                      data-testid={`permission-hrs-${r.id}`}
                    />
                    <div className="flex space-x-2">
                      <button onClick={() => action(r.id, 'approve')} className="px-3 py-2 bg-green-600 text-white rounded text-sm" data-testid={`permission-confirm-${r.id}`}>Confirm</button>
                      <button onClick={() => setEditing(null)} className="px-3 py-2 bg-gray-200 rounded text-sm">Cancel</button>
                    </div>
                  </div>
                ) : (
                  <div className="flex space-x-2">
                    <button onClick={() => { setEditing(r.id); setHrs(r.actual_hours || r.requested_hours); }} className="px-3 py-2 bg-green-600 text-white rounded-lg text-sm flex items-center space-x-1" data-testid={`permission-approve-${r.id}`}>
                      <CheckCircle size={14} /><span>Approve</span>
                    </button>
                    <button onClick={() => action(r.id, 'reject')} className="px-3 py-2 bg-red-600 text-white rounded-lg text-sm flex items-center space-x-1" data-testid={`permission-reject-${r.id}`}>
                      <XCircle size={14} /><span>Reject</span>
                    </button>
                  </div>
                )
              )}
            </div>
          );
        })}
      </div>
      )}

      {previewPhoto && (
        <div className="fixed inset-0 bg-black bg-opacity-80 z-50 flex items-center justify-center p-4" onClick={() => setPreviewPhoto(null)} data-testid="permission-photo-preview">
          <img src={previewPhoto} alt="slip" className="max-h-[90vh] max-w-full rounded-lg" />
        </div>
      )}
    </div>
  );
}
